'use client';

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
// Its own entry: the illustration's asset map stays out of the web barrel.
import { CharacterIllustration } from '@galapagos/design-system/character-illustration.web';
import { Button, TextButton } from '@galapagos/design-system/web';
import { signupClientIdSchema, type SignupResumeView } from '@galapagos/shared';
import { FormActions } from '../../components/FormActions';
import { PageTitle } from '../../components/PageTitle';
import { ensureSignupStepStyle } from '../../components/signup-step-style';
import { useLegacySignupParams } from '../../hooks/useLegacySignupParams';
import { useSignupApi } from '../../hooks/useSignupApi';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import { useStepHeading } from '../../hooks/useStepHeading';
import { parseLegacySignupParams } from '../../services/legacy-params';
import {
  NOTHING_TO_RESUME,
  readSignupResumeWithin,
  resumeQuery,
  welcomeBackContinueStep,
} from '../../services/signup-resume';
import { firstSignupStep, signupPathFor } from '../../steps';
import { useSignupFlowStore } from '../../stores/signup-flow-store';
import { ensureSignupOffPathStyle } from './off-path-style';

type WelcomeBackAction = 'continue' | 'start-over';

/**
 * v9 4.3: a signed-in member with no saved details, welcomed back. Signup
 * kept none of their earlier answers (they lived in the browser), so both
 * actions start the flow again with the sign-in kept: Continue restores the
 * employer and legal name from their own latest eligibility check, Start over
 * restores only the employer. The employer falls back to the login link's own
 * `client_id`; with neither, the member searches for it on the no-employer
 * path. Without a sign-in contact in this tab (a cold visit) both actions
 * start an ordinary flow that asks for the account again.
 *
 * The name is PHI: the tab title stays generic and the heading sits in a
 * `ph-no-capture` container, so replay and autocapture skip it.
 */
export function WelcomeBackContent() {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  const queryClient = useQueryClient();
  const linkParams = useLegacySignupParams();
  const currentQuery = useSearchParams().toString();
  const { goToStepWithQuery } = useSignupNavigation();
  // Keyed by the flow the page opened in, so a later member in this tab never
  // sees this one's read, and starting the resumed flow does not read again.
  const [openedEpoch] = useState(() => useSignupFlowStore.getState().flowEpoch);
  // With no one signed in (a cold visit) there is nothing to read.
  const [signedIn] = useState(() => useSignupFlowStore.getState().signInContact !== null);
  const resumeRead = {
    queryKey: ['signup', 'resume', openedEpoch] as const,
    queryFn: () => readSignupResumeWithin(api),
    staleTime: Infinity,
    gcTime: 0,
  };
  const resume = useQuery({ ...resumeRead, enabled: signedIn });
  // Read when the press resolves, not when it began: a language switch while
  // waiting writes a new `lng` that the next page must keep.
  const queryRef = useRef(currentQuery);
  useEffect(() => {
    queryRef.current = currentQuery;
  }, [currentQuery]);
  // A press that resolves after the member left this page (the footer's Book a
  // call, say) or after the flow restarted must not move them anywhere.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  const genericTitle = t('welcomeBack.title');
  const firstName = resume.data?.firstName;
  const title = firstName ? t('welcomeBack.titleNamed', { firstName }) : genericTitle;
  // Focus follows the generic title, so a name arriving after the member has
  // tabbed to a button does not pull focus back to the heading.
  const headingRef = useStepHeading(genericTitle);
  const [pressed, setPressed] = useState<WelcomeBackAction | null>(null);
  // A ref, not the state above, guards re-entry: two presses in one tick both
  // see the state before it updates.
  const pressedRef = useRef(false);
  ensureSignupStepStyle();
  ensureSignupOffPathStyle();

  const go = async (action: WelcomeBackAction) => {
    if (pressedRef.current) return;
    pressedRef.current = true;
    setPressed(action);
    // Waits at most the read's own bound, then goes on without it.
    const read: SignupResumeView = signedIn
      ? await queryClient.ensureQueryData(resumeRead)
      : NOTHING_TO_RESUME;
    if (!liveRef.current) return;
    if (useSignupFlowStore.getState().flowEpoch !== openedEpoch) {
      pressedRef.current = false;
      setPressed(null);
      return;
    }
    // A reference that is not a signup link id is treated as no employer.
    const checkClientId = signupClientIdSchema.safeParse(read.clientRef).success
      ? (read.clientRef ?? undefined)
      : undefined;
    const clientId = checkClientId ?? linkParams.clientId;
    // The link's own sub-employer stays when the link's own employer is used.
    const query = resumeQuery(queryRef.current, clientId, checkClientId === undefined);
    const params = parseLegacySignupParams(query);
    const path = signupPathFor(params);
    const store = useSignupFlowStore.getState();
    const signInContact = store.signInContact;
    if (signInContact === null) {
      store.resetFlow();
      goToStepWithQuery(firstSignupStep(path), query);
      return;
    }
    const legalName =
      action === 'continue' && read.firstName && read.lastName
        ? { firstName: read.firstName, lastName: read.lastName }
        : null;
    store.beginResumedSignupFlow({ params, signInContact, legalName });
    const target =
      action === 'continue'
        ? welcomeBackContinueStep(path, useSignupFlowStore.getState())
        : firstSignupStep(path);
    goToStepWithQuery(target, query);
  };

  return (
    <div className="signup-step">
      <div className="signup-off-path__illustration" data-testid="signup-welcome-back-illustration">
        <CharacterIllustration name="looking-ahead-sm" size="M" />
      </div>
      <div className="ph-no-capture" data-testid="signup-welcome-back-capture-guard">
        <PageTitle title={title} description={t('welcomeBack.body')} headingRef={headingRef} />
      </div>
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('welcomeBack.continue')}
          state={pressed === 'continue' ? 'Loading' : 'Default'}
          onPress={() => void go('continue')}
          actionId="signup.login-no-progress-continue.activate"
          data-testid="signup-welcome-back-continue"
        />
        <TextButton
          label={t('welcomeBack.startOver')}
          loading={pressed === 'start-over'}
          onPress={() => void go('start-over')}
          actionId="signup.login-no-progress-start-over.activate"
          data-testid="signup-welcome-back-start-over"
        />
      </FormActions>
    </div>
  );
}
